import type { AxisSpec, SamplerSymmetryPolicy } from "./types";

const CENTRE_TOLERANCE = 1e-8;

export const BETA_REFLECTION_ODD_INPUTS = ["da", "dr", "P", "R"] as const;

const INPUT_LABELS: Record<(typeof BETA_REFLECTION_ODD_INPUTS)[number], string> = {
  da: "aileron (delta-a)",
  dr: "rudder (delta-r)",
  P: "roll rate (P)",
  R: "yaw rate (R)",
};

export type BetaReflectionEligibility = {
  eligible: boolean;
  reason: string;
  invalidInputs: string[];
};

export type BetaSliceSymmetry = {
  kind: "neutral" | "paired-slice" | "joint-plane" | "beta-not-plotted";
  message: string;
  asymmetricInputs: string[];
};

function closeTo(value: number, target: number, scale = 1): boolean {
  return Math.abs(value - target) <= CENTRE_TOLERANCE * Math.max(1, Math.abs(scale));
}

function axisBounds(axis: AxisSpec): [number, number] | null {
  if (!Number.isFinite(axis.min) || !Number.isFinite(axis.max)) return null;
  return [axis.min as number, axis.max as number];
}

function reflectionClosedRange(axis: AxisSpec, centre: number): boolean {
  const bounds = axisBounds(axis);
  if (!bounds) return false;
  const [minimum, maximum] = bounds;
  return maximum > minimum && closeTo(minimum + maximum, 2 * centre, maximum - minimum);
}

function fixedValue(axis: AxisSpec): number | null {
  if (Number.isFinite(axis.fixedValue)) return axis.fixedValue as number;
  const bounds = axisBounds(axis);
  if (!bounds || !closeTo(bounds[0], bounds[1])) return null;
  return bounds[0];
}

/**
 * A beta reflection is a joint aircraft-state transform, not a promise that
 * every fixed-control beta trace is even/odd. The beta domain and every odd
 * lateral input domain must therefore be closed under sign reversal. A
 * lateral input omitted from the problem is treated as an implicit neutral
 * held condition.
 */
export function betaReflectionEligibility(
  inputs: readonly AxisSpec[],
  centre = 0,
): BetaReflectionEligibility {
  if (!Number.isFinite(centre) || !closeTo(centre, 0)) {
    return {
      eligible: false,
      reason: "Aircraft reflection symmetry is defined only about beta = 0.",
      invalidInputs: ["centre"],
    };
  }
  const beta = inputs.find((axis) => axis.name === "beta");
  if (!beta || beta.mode !== "range") {
    return {
      eligible: false,
      reason: "Beta reflection needs beta to be a sampled range.",
      invalidInputs: ["beta"],
    };
  }
  if (!reflectionClosedRange(beta, centre)) {
    return {
      eligible: false,
      reason: "Beta reflection needs beta bounds paired about 0.",
      invalidInputs: ["beta"],
    };
  }

  const invalid: string[] = [];
  for (const name of BETA_REFLECTION_ODD_INPUTS) {
    const axis = inputs.find((item) => item.name === name);
    if (!axis) continue;
    if (axis.mode === "range") {
      if (!reflectionClosedRange(axis, 0)) invalid.push(name);
      continue;
    }
    const value = fixedValue(axis);
    if (value == null || !closeTo(value, 0)) invalid.push(name);
  }
  if (invalid.length > 0) {
    const labels = invalid.map((name) => INPUT_LABELS[name as keyof typeof INPUT_LABELS] ?? name);
    return {
      eligible: false,
      reason: `Beta reflection needs ${labels.join(" and ")} neutral when fixed, or sampled over paired positive/negative bounds.`,
      invalidInputs: invalid,
    };
  }
  return {
    eligible: true,
    reason: "The beta and lateral-control domains are closed under the coupled reflection.",
    invalidInputs: [],
  };
}

/** Defensive request-boundary filter. Invalid beta policies never reach the
 * Tailor runtime even when a caller constructs a CampaignConfig directly. */
export function validSymmetryPolicies(
  inputs: readonly AxisSpec[],
  policies: readonly SamplerSymmetryPolicy[],
): SamplerSymmetryPolicy[] {
  return policies
    .filter((policy) => policy.enabled && (policy.mirrorObservations || policy.sampleHalf))
    // The Mannequin runtime owns one physical transform. Do not forward a
    // generic mathematical "even axis" declaration as aircraft physics.
    .filter((policy) => policy.axisName === "beta")
    .filter((policy) => betaReflectionEligibility(inputs, policy.centre ?? 0).eligible)
    .map((policy) => ({ ...policy }));
}

function signedControl(name: string, value: number): string {
  const label = name === "da" ? "delta-a" : name === "dr" ? "delta-r" : name;
  return `${label}=${Number(value.toFixed(3))}`;
}

/** Describes what reflection means for the currently displayed slice. */
export function betaSliceSymmetry(
  heldValues: Readonly<Record<string, number>>,
  plottedAxes: readonly string[],
): BetaSliceSymmetry {
  if (!plottedAxes.includes("beta")) {
    return {
      kind: "beta-not-plotted",
      message: "The coupled beta-reflection prior is active, but beta is not plotted in this slice.",
      asymmetricInputs: [],
    };
  }

  const jointAxes = BETA_REFLECTION_ODD_INPUTS.filter((name) => plottedAxes.includes(name));
  const asymmetric = BETA_REFLECTION_ODD_INPUTS.filter((name) => {
    if (plottedAxes.includes(name)) return false;
    const value = heldValues[name];
    return typeof value === "number" && Number.isFinite(value) && !closeTo(value, 0);
  });
  if (asymmetric.length > 0) {
    const current = asymmetric.map((name) => signedControl(name, heldValues[name] ?? 0)).join(", ");
    const partner = asymmetric.map((name) => signedControl(name, -(heldValues[name] ?? 0))).join(", ");
    const jointMove = jointAxes.length > 0
      ? ` while also flipping beta and ${jointAxes.map((name) => INPUT_LABELS[name]).join(" / ")} together`
      : "";
    return {
      kind: "paired-slice",
      message: `Not a reflection-closed slice: ${current}. Reflection relates this plot to the separate slice at ${partner}${jointMove}; it does not mirror beta at the same held controls.`,
      asymmetricInputs: [...asymmetric],
    };
  }

  if (jointAxes.length > 0) {
    return {
      kind: "joint-plane",
      message: `Joint reflection: flip beta and ${jointAxes.map((name) => INPUT_LABELS[name]).join(" / ")} together. This surface is not symmetric about beta alone.`,
      asymmetricInputs: [...jointAxes],
    };
  }

  return {
    kind: "neutral",
    message: "Neutral lateral state: beta reflection stays within this slice. CL, CD and Cm are even; CY, Cl and Cn are odd.",
    asymmetricInputs: [],
  };
}
